import React from 'react';
import {
  addDoc,
  collection,
  doc,
  getDoc,
  onSnapshot,
  Timestamp,
  Unsubscribe,
  updateDoc,
} from 'firebase/firestore';
import { useRouter } from 'next/router';
import { firestore } from '../lib';
import { Application } from '../models';
import { useUser } from '../hooks/useUser';

type TApplicationContext = {
  application: Application;
  setApplication: React.Dispatch<React.SetStateAction<Application>>;
};

export const ApplicationContext = React.createContext<TApplicationContext>({
  application: undefined,
  setApplication: undefined,
});

interface ApplicationContextProviderProps {
  children: React.ReactNode;
}

async function getOrCreateApplication(userId: string, applicationId: string) {
  if (typeof userId !== 'string') {
    return null;
  }
  if (typeof applicationId !== 'string') {
    return null;
  }
  const application = await getDoc(
    doc(firestore, 'applications', applicationId)
  );
  if (application.exists() && application.data().archive === false) {
    return application.data();
  }
  const newApplication = await addDoc(collection(firestore, 'applications'), {
    user: userId,
    startDate: Timestamp.fromDate(new Date()),
    step: '1',
    status: 'progress',
    archive: false,
  });
  await Promise.all([
    updateDoc(doc(firestore, 'applications', newApplication.id), {
      id: newApplication.id,
    }),
    updateDoc(doc(firestore, 'users', userId), {
      application: newApplication.id,
    }),
  ]);
  return newApplication;
}

export function ApplicationContextProvider({
  children,
}: ApplicationContextProviderProps) {
  const [user] = useUser();
  const [application, setApplication] = React.useState<Application>();
  const router = useRouter();

  React.useEffect(
    function syncApplication() {
      let unsubscribe: Unsubscribe | null = null;
      const query = async () => {
        if (user?.privilege === 'applicant' && user?.application) {
          const _application = await getOrCreateApplication(
            user.id,
            user.application
          );
          if (_application !== null) {
            unsubscribe = onSnapshot(
              doc(firestore, 'applications', _application.id),
              (snapshot) => {
                if (snapshot.exists()) {
                  setApplication({
                    id: user?.application ?? null,
                    ...(snapshot.data() ?? {}),
                  } as Application);
                } else {
                  setApplication({
                    id: user?.application ?? null,
                  } as Application);
                }
              }
            );
          }
        } else if (
          user?.privilege === 'admin' ||
          user?.privilege === 'reviewer'
        ) {
          router.push('/admin/applications');
        }
      };
      query();
      return () => {
        if (unsubscribe) {
          unsubscribe();
        }
      };
    },
    [user?.id, user?.application, user?.privilege]
  );

  const value = React.useMemo(
    () => ({ application, setApplication }),
    [application]
  );
  return (
    <ApplicationContext.Provider value={value}>
      {children}
    </ApplicationContext.Provider>
  );
}
